import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { KnowledgeNoteForm } from "./KnowledgeNoteForm";
import {
  useKnowledgeNoteMutations,
  type ContractorKnowledgeNote,
} from "../api/knowledgeNotes";

/**
 * Lista zasad podpowiadania (karta firmy albo rejestr zbiorczy) z przełącznikiem
 * aktywności, edycją inline i usuwaniem. `canManage` = bramka zapisu (lustro backendu).
 * Wyłączona zasada zostaje w bazie (z treścią i zakresem), ale backend pomija ją przy
 * podpowiedzi (`applicable_notes` filtruje `is_active`) — dopóki ktoś jej nie włączy.
 */

/** Przełącznik „zasada aktywna" — ten sam wygląd, co przełączniki w Ustawieniach. */
function ActiveSwitch({
  active,
  pending,
  onToggle,
}: {
  active: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex flex-shrink-0 items-center gap-2 pt-0.5">
      <span className="text-[11px] text-[var(--color-text-secondary)]">
        {active ? "Aktywna" : "Wyłączona"}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={active}
        aria-label="Zasada aktywna"
        title={
          active
            ? "Wyłącz — zasada zostanie zapisana, ale przestanie wpływać na podpowiedzi"
            : "Włącz — zasada znów będzie brana pod uwagę w podpowiedziach"
        }
        disabled={pending}
        onClick={onToggle}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-wait disabled:opacity-60 ${
          active ? "bg-[var(--color-primary)]" : "bg-gray-300 dark:bg-gray-600"
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
            active ? "translate-x-[18px]" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

const EFFECT_CLASS: Record<string, string> = {
  advise: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  exclude: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  prefer: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
};

function windowLabel(n: ContractorKnowledgeNote): string {
  if (!n.valid_from && !n.valid_until) return "";
  const from = n.valid_from ? new Date(n.valid_from).toLocaleDateString("pl-PL") : "";
  const until = n.valid_until ? new Date(n.valid_until).toLocaleDateString("pl-PL") : "";
  if (from && until) return `${from} – ${until}`;
  return from ? `od ${from}` : `do ${until}`;
}

export function KnowledgeNotesList({
  notes,
  canManage,
  showContractor = false,
  emptyText = "Brak zasad.",
  fixedContractorId,
}: {
  notes: ContractorKnowledgeNote[];
  canManage: boolean;
  showContractor?: boolean;
  emptyText?: string;
  fixedContractorId?: string;
}) {
  const { update, toggle, remove } = useKnowledgeNoteMutations();
  const [editingId, setEditingId] = useState<string | null>(null);

  if (notes.length === 0) {
    return <p className="py-3 text-sm text-[var(--color-text-secondary)]">{emptyText}</p>;
  }

  return (
    <ul className="divide-y divide-[var(--color-border)]" data-testid="knowledge-notes-list">
      {notes.map((n) => {
        const toggling = toggle.isPending && toggle.variables?.id === n.id;
        const toggleFailed = toggle.isError && toggle.variables?.id === n.id;
        // While the switch is saving, show the state it is going to — not the old one.
        const active = toggling ? (toggle.variables?.is_active ?? n.is_active) : n.is_active;
        return (
          <li key={n.id} className="py-3">
            {editingId === n.id ? (
              <KnowledgeNoteForm
                initial={n}
                fixedContractorId={fixedContractorId}
                pending={update.isPending}
                error={update.isError ? "Nie udało się zapisać zasady." : null}
                onCancel={() => setEditingId(null)}
                onSubmit={(payload) =>
                  update.mutate({ id: n.id, ...payload }, { onSuccess: () => setEditingId(null) })
                }
              />
            ) : (
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className={`rounded px-1.5 py-0.5 font-medium ${EFFECT_CLASS[n.effect] ?? ""}`}>
                      {n.effect_label}
                    </span>
                    <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">
                      {n.scope_label}
                    </span>
                    {showContractor && (
                      <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">
                        {n.contractor ? (
                          <Link to={`/contractors/${n.contractor}`} className="underline hover:text-[var(--color-primary)]">
                            {n.contractor_name}
                          </Link>
                        ) : (
                          "wszystkie firmy"
                        )}
                      </span>
                    )}
                    {n.project_name && (
                      <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">
                        {n.project_name}
                      </span>
                    )}
                    {windowLabel(n) && (
                      <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">
                        {windowLabel(n)}
                      </span>
                    )}
                    {!n.is_active && (
                      <span className="rounded bg-gray-200 px-1.5 py-0.5 font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        wyłączona — pomijana w podpowiedziach
                      </span>
                    )}
                    {n.is_active && !n.in_force && (
                      <span className="text-[var(--color-text-muted)]">poza oknem obowiązywania</span>
                    )}
                  </div>
                  <p
                    className={`whitespace-pre-wrap text-sm ${
                      n.is_active
                        ? "text-[var(--color-text-primary)]"
                        : "text-[var(--color-text-muted)] line-through"
                    }`}
                  >
                    {n.body}
                  </p>
                  <div className="flex items-center gap-3 text-[11px] text-[var(--color-text-muted)]">
                    <span>
                      {n.created_by_name} · {new Date(n.created_at).toLocaleDateString("pl-PL")}
                    </span>
                    {canManage && (
                      <>
                        <button
                          type="button"
                          className="underline hover:text-[var(--color-primary)]"
                          onClick={() => setEditingId(n.id)}
                        >
                          Edytuj
                        </button>
                        <button
                          type="button"
                          className="underline hover:text-red-500"
                          onClick={() => {
                            if (window.confirm("Usunąć tę zasadę?")) remove.mutate(n.id);
                          }}
                        >
                          Usuń
                        </button>
                      </>
                    )}
                  </div>
                  {toggleFailed && (
                    <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">
                      Nie udało się {toggle.variables?.is_active ? "włączyć" : "wyłączyć"} zasady —
                      spróbuj ponownie.
                    </p>
                  )}
                </div>
                {canManage && (
                  <ActiveSwitch
                    active={active}
                    pending={toggling}
                    onToggle={() => toggle.mutate({ id: n.id, is_active: !n.is_active })}
                  />
                )}
              </div>
            )}
          </li>
        );
      })}
      {remove.isError && (
        <li className="py-2">
          <Button variant="ghost" size="sm" onClick={() => remove.reset()}>
            Nie udało się usunąć zasady — spróbuj ponownie
          </Button>
        </li>
      )}
    </ul>
  );
}
